'use client';

import { useEffect, useRef } from 'react';
import { ACCOUNT_WHITE_LIST_NEW } from '@/utils/constants';
import logger from '@/utils/client-logger';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/providers/auth-provider';

/**
 * 用于 /account/* 等需要授权页面的链接
 * 自动禁用 prefetch，避免缓存未授权响应
 *
 * 问题背景：
 * 未登录用户访问需要授权的页面时，middleware 会重定向。
 * Next.js 的 Link 组件默认会 prefetch，导致重定向响应被缓存。
 * 用户登录后再点击链接时，使用缓存的响应导致路由无法跳转。
 *
 * 解决方案：
 * 对于需要授权的页面链接，禁用 prefetch 即可从源头解决问题。
 * 重写 router.prefetch 逻辑
 */
export function useRewriteRouterPrefetch() {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const originalPrefetch = useRef(router.prefetch);

  useEffect(() => {
    // 重写 prefetch 方法
    router.prefetch = (href, options): void => {
      if (!href) return originalPrefetch.current(href, options);

      // 保持和 middleware 的逻辑一致
      const isAccountPath = href.includes('/account');
      const isProtectedPath =
        isAccountPath && !ACCOUNT_WHITE_LIST_NEW.some((whitePath) => href.includes(whitePath));

      // 需要授权的路径且用户未登录，不执行预取操作
      if (isProtectedPath && !isAuthenticated) {
        logger.info(`[Router Prefetch] ignored. href: ${href}`);
        return;
      }

      originalPrefetch.current(href, options);
    };

    // 组件卸载时恢复原始方法
    return () => {
      router.prefetch = originalPrefetch.current;
    };
  }, [router, isAuthenticated]);
}
